import {css} from 'styled-components';

import {ReactNode} from 'react';
import {Link} from 'react-router-dom';

import {ICellRendererParams} from '../../../types/AgGridTypes';
import {LinkPropsGetter} from '../../../types/LinkPropsGetter';

interface MetaTextCellRendererProps extends ICellRendererParams {
  getLinkProps?: LinkPropsGetter;
}
const META_REGEX = /@\[([^\]]+)\]\(([^)]+)\)/g;

const stopPropagationOnRef = (ref: HTMLAnchorElement | null) => {
  if (!ref) {
    return;
  }

  ref.onclick = (event) => event.stopPropagation();
};

export const getMetaTextValue = (props: MetaTextCellRendererProps): ReactNode[] => {
  const cellValue = props.getValue ? props.getValue() : props.value;

  if (typeof cellValue !== 'string') {
    return [cellValue];
  }

  const segments: (string | ReactNode)[] = [];
  let lastIndex = 0;

  const matches = Array.from(cellValue.matchAll(META_REGEX));

  matches.forEach((match, index) => {
    const matchStart = match.index!;
    const matchEnd = matchStart + match[0].length;

    // Add text before the match
    if (matchStart > lastIndex) {
      segments.push(cellValue.substring(lastIndex, matchStart));
    }

    const name = match[1];
    const id = match[2];

    const linkProps = props.getLinkProps
      ? props.getLinkProps?.({data: {id, ...props.data}})
      : undefined;

    // Render as link if linkProps provided, otherwise as plain text
    if (linkProps?.to) {
      segments.push(
        <Link {...linkProps} ref={stopPropagationOnRef} key={`meta-${index}-${id}`}>
          {name}
        </Link>
      );
    } else {
      segments.push(
        <div
          key={`meta-${index}-${id}`}
          css={css`
            background-color: ${({theme}) => theme.colors.palettes.blue[30][100]};
            display: inline;
          `}
        >
          {name}
        </div>
      );
    }

    lastIndex = matchEnd;
  });

  // Add remaining text after the last match
  if (lastIndex < cellValue.length) {
    segments.push(cellValue.substring(lastIndex));
  }

  return segments;
};
